import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
} from '@mui/material';

const conditions = [
  'Depression',
  'Anxiety',
  'ADHD',
  'Bipolar Disorder',
  'PTSD & Trauma',
  'Mood Disorders',
  'OCD',
  'Opioid Use Disorder',
];

const careOptions = [
  'In-Person & Telehealth Psychiatry',
  'ADHD Testing & Assessments',
  'Medication Management',
  'Depression & Anxiety Treatment',
];

const services = [
  {
    title: 'Psychiatry',
    description:
      'Personalized psychiatry services and medication management for depression, anxiety, ADHD, bipolar disorder, mood disorders, and other mental health concerns.',
    link: '/psychiatry',
  },
  {
    title: 'Telehealth',
    description:
      'Secure telehealth psychiatry appointments for patients in Texas and Minnesota who want convenient mental health care from home.',
    link: '/telehealth',
  },
  {
    title: 'ADHD Testing & Assessments',
    description:
      'Comprehensive ADHD evaluations using advanced testing tools to better understand attention, focus, impulsivity, and cognitive concerns.',
    links: [
      { label: 'QbTest®', path: '/qbtest' },
      { label: 'Creyos', path: '/creyos' },
    ],
  },
  {
    title: 'SPRAVATO® Treatment',
    description:
      'SPRAVATO® treatment for adults with treatment-resistant depression in a safe, supportive, and professionally monitored setting.',
    link: '/esketamine',
  },
  {
    title: 'Suboxone® Treatment',
    description:
      'Medication-assisted treatment for opioid use disorder to help reduce cravings, manage withdrawal symptoms, and support long-term recovery.',
    link: '/suboxone',
  },
  {
    title: 'TMS Therapy',
    description:
      'TMS therapy is a non-invasive treatment for depression that uses magnetic stimulation when medication has not provided enough relief.',
    link: '/tms',
  },
];

const ServiceHighlights = () => (
  <Box
    sx={{
      backgroundColor: '#f9faf7',
      py: { xs: 5, md: 8 },
      px: { xs: 2, sm: 3 },
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}
  >
    {/* Intro */}
    <Box
      sx={{
        textAlign: 'center',
        mb: 5,
        maxWidth: '1000px',
        mx: 'auto',
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#2e3a11',
          fontSize: { xs: '2rem', md: '2.6rem' },
        }}
      >
        Mental Health Care Designed Around You
      </Typography>

      <Typography
        variant="body1"
        sx={{
          maxWidth: '900px',
          mx: 'auto',
          lineHeight: 1.9,
          mb: 6,
          color: '#4b4b4b',
          fontSize: { xs: '1rem', sm: '1.1rem' },
        }}
      >
        Aspire Behavioral Health provides compassionate psychiatric care and mental
        health services for patients in San Antonio and across Texas. Our team offers
        medication management, ADHD testing, anxiety treatment, depression treatment,
        telehealth psychiatry, and advanced mental health treatment options designed
        around your individual needs.
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mt: 2,
          fontWeight: 500,
          color: '#2e3a11',
        }}
      >
        Call{' '}
        <a href="tel:2108669850" style={{ color: 'inherit' }}>
          210-866-9850
        </a>{' '}
        or{' '}
        <Link to="/schedule-appointment" style={{ color: 'inherit' }}>
          request an appointment online
        </Link>
        .
      </Typography>
    </Box>

    {/* SEO boxes */}
    <Box
      sx={{
        maxWidth: '1000px',
        mx: 'auto',
        mb: 6,
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {careOptions.map((option) => (
        <Box
          key={option}
          sx={{
            backgroundColor: '#dfe8d3',
            color: '#2e3a11',
            borderRadius: 3,
            minHeight: 90,
            px: 2,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            fontWeight: 700,
            lineHeight: 1.5,
            border: '1px solid rgba(58,76,23,0.12)',
            boxShadow: '0px 3px 10px rgba(0,0,0,0.05)',
          }}
        >
          {option}
        </Box>
      ))}
    </Box>

    {/* Conditions */}
    <Box
      sx={{
        maxWidth: '1100px',
        mx: 'auto',
        mb: 7,
        p: { xs: 3, md: 4 },
        borderRadius: 4,
        backgroundColor: '#eef3e6',
        border: '1px solid rgba(58,76,23,0.10)',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 1,
          color: '#2e3a11',
        }}
      >
        Conditions We Treat
      </Typography>

      <Typography
        variant="body2"
        sx={{
          maxWidth: '760px',
          mx: 'auto',
          mb: 3,
          lineHeight: 1.8,
          color: '#4b4b4b',
        }}
      >
        Our providers treat a wide range of mental health conditions, including
        anxiety disorders, depression, ADHD, PTSD, bipolar disorder, OCD, mood
        disorders, and opioid use disorder through personalized psychiatric care
        and medication management.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        {conditions.map((condition) => (
          <Chip
            key={condition}
            label={condition}
            sx={{
              backgroundColor: 'white',
              color: '#2e3a11',
              fontWeight: 600,
              border: '1px solid rgba(58,76,23,0.15)',
              px: 1,
            }}
          />
        ))}
      </Box>
    </Box>

    {/* Services */}
    <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          fontWeight: 700,
          mb: 4,
          color: '#2e3a11',
        }}
      >
        Mental Health Services at Aspire Behavioral Health
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {services.map((service) => (
          <Grid
            item
            xs={12}
            sm={6}
            lg={4}
            key={service.title}
            sx={{ display: 'flex' }}
          >
            <Card
              sx={{
                width: '100%',
                minHeight: 340,
                backgroundColor: '#eef3e6',
                color: '#2e3a11',
                borderRadius: 4,
                border: '1px solid rgba(58,76,23,0.10)',
                boxShadow: '0px 4px 12px rgba(0,0,0,0.06)',
              }}
            >
              <CardContent
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 4,
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    {service.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.9,
                      color: '#4b4b4b',
                    }}
                  >
                    {service.description}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mt: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  {service.links ? (
                    service.links.map((link) => (
                      <Button
                        key={link.path}
                        component={Link}
                        to={link.path}
                        variant="contained"
                        sx={buttonStyle}
                      >
                        {link.label}
                      </Button>
                    ))
                  ) : (
                    <Button
                      component={Link}
                      to={service.link}
                      variant="contained"
                      sx={buttonStyle}
                    >
                      Learn More
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  </Box>
);

const buttonStyle = {
  backgroundColor: '#dfe8d3',
  color: '#2e3a11',
  fontWeight: 700,
  borderRadius: '25px',
  px: 3,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#d3dfc3',
    boxShadow: 'none',
  },
};

export default ServiceHighlights;